import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Video,
  FileEdit,
  Lock,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Image,
  Youtube,
  Eye,
  ArrowLeft,
  Upload,
  User,
  MessageCircle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper to extract YouTube video ID (supports long-form, short-form, shorts, and embed URLs)
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
};

// Detect embeddable URL — YouTube, Vimeo, Facebook. Falls back to null so
// the UI can decide whether to render <video src="…"> for direct MP4/HLS.
const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = getYouTubeEmbedUrl(url);
  if (yt) return yt;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  if (/facebook\.com\/.*\/videos\/|fb\.watch\//i.test(url)) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  }
  return null;
};

// Detect direct video file URLs so we can play them via <video>
const isDirectVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m3u8)(\?.*)?$/i.test(url || '');

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('training');
  const imageInputRef = useRef(null);
  const founderImageInputRef = useRef(null);
  
  // Training Video State
  const [trainingVideoUrl, setTrainingVideoUrl] = useState('');
  const [savingVideo, setSavingVideo] = useState(false);
  
  // WhatsApp Group Link State
  const [whatsAppGroupLink, setWhatsAppGroupLink] = useState('');
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);
  
  // Credentials State
  const [credentials, setCredentials] = useState({
    new_username: '',
    new_password: '',
    confirm_password: '',
    current_password: ''
  });
  const [savingCredentials, setSavingCredentials] = useState(false);
  
  // Founder Image State
  const [founderImageUrl, setFounderImageUrl] = useState('');
  const [uploadingFounder, setUploadingFounder] = useState(false);
  
  // Testimonial Image Upload State
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Landing Page State
  const [landingPage, setLandingPage] = useState({
    headline: '',
    subheadline: '',
    hero_description: '',
    hero_video_url: '',
    benefits: [],
    sales_copy: '',
    cta_button_text: '',
    meta_pixel_id: '',
    tracking_head_code: '',
    testimonials: []
  });
  const [savingLandingPage, setSavingLandingPage] = useState(false);
  const [savingHeroVideo, setSavingHeroVideo] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ type: 'youtube', url: '', caption: '' });
  const [newBenefit, setNewBenefit] = useState({ title: '', description: '' });

  useEffect(() => {
    fetchSettings();
    fetchLandingPage();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminAPI.getSettings();
      setTrainingVideoUrl(response.data.training_video_url || '');
      setWhatsAppGroupLink(response.data.whatsapp_group_link || '');
      if (response.data.founder_image_url) {
        setFounderImageUrl(response.data.founder_image_url);
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchLandingPage = async () => {
    try {
      const response = await adminAPI.getLandingPage();
      setLandingPage(response.data);
      if (response.data.founder_image_url) {
        setFounderImageUrl(response.data.founder_image_url);
      }
    } catch (error) {
      console.error('Failed to load landing page');
    }
  };

  const handleSaveTrainingVideo = async () => {
    setSavingVideo(true);
    try {
      await adminAPI.updateTrainingVideo(trainingVideoUrl);
      toast.success('Training video updated successfully!');
    } catch (error) {
      toast.error('Failed to update training video');
    } finally {
      setSavingVideo(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    setSavingWhatsApp(true);
    try {
      await adminAPI.updateWhatsAppGroup(whatsAppGroupLink);
      toast.success('WhatsApp group link updated!');
    } catch (error) {
      toast.error('Failed to update WhatsApp link');
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const handleFounderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingFounder(true);
    try {
      const response = await adminAPI.uploadFounderImage(file);
      setFounderImageUrl(response.data.url);
      toast.success('Founder image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload founder image');
    } finally {
      setUploadingFounder(false);
    }
  };

  const handleTestimonialImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const response = await adminAPI.uploadImage(file);
      setNewTestimonial(prev => ({ ...prev, type: 'image', url: response.data.url }));
      toast.success('Image uploaded! Now add a caption and click "Add Testimonial"');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateCredentials = async () => {
    if (credentials.new_password !== credentials.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (credentials.new_password.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    if (!credentials.current_password) {
      toast.error('Please enter your current password');
      return;
    }

    setSavingCredentials(true);
    try {
      await adminAPI.updateCredentials({
        new_username: credentials.new_username,
        new_password: credentials.new_password,
        current_password: credentials.current_password
      });
      toast.success('Credentials updated! Please log in again with your new credentials.');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleSaveHeroVideo = async () => {
    setSavingHeroVideo(true);
    try {
      await adminAPI.updateLandingPage({ hero_video_url: landingPage.hero_video_url || '' });
      if (!landingPage.hero_video_url) {
        toast.success('Video removed from landing page');
      } else if (getEmbedUrl(landingPage.hero_video_url) || isDirectVideoUrl(landingPage.hero_video_url)) {
        toast.success('Video applied — visitors will see it at the top of the landing page');
      } else {
        toast.success('Saved, but this URL may not be a valid video link. Check the preview below.');
      }
    } catch (error) {
      toast.error('Failed to apply video');
    } finally {
      setSavingHeroVideo(false);
    }
  };

  const handleSaveLandingPage = async () => {
    setSavingLandingPage(true);
    try {
      await adminAPI.updateLandingPage({
        headline: landingPage.headline,
        subheadline: landingPage.subheadline,
        hero_description: landingPage.hero_description,
        hero_video_url: landingPage.hero_video_url,
        benefits: landingPage.benefits,
        sales_copy: landingPage.sales_copy,
        cta_button_text: landingPage.cta_button_text,
        cta_button_link: landingPage.cta_button_link,
        meta_pixel_id: landingPage.meta_pixel_id,
        tracking_head_code: landingPage.tracking_head_code,
      });
      toast.success('Landing page updated successfully!');
    } catch (error) {
      toast.error('Failed to update landing page');
    } finally {
      setSavingLandingPage(false);
    }
  };

  const handleAddTestimonial = async () => {
    if (!newTestimonial.url) {
      toast.error('Please enter a URL');
      return;
    }
    try {
      const response = await adminAPI.addTestimonial({
        type: newTestimonial.type,
        url: newTestimonial.url,
        caption: newTestimonial.caption,
        order: landingPage.testimonials?.length || 0
      });
      setLandingPage(prev => ({
        ...prev,
        testimonials: [...(prev.testimonials || []), response.data.testimonial]
      }));
      setNewTestimonial({ type: 'youtube', url: '', caption: '' });
      toast.success('Testimonial added!');
    } catch (error) {
      toast.error('Failed to add testimonial');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    try {
      await adminAPI.deleteTestimonial(id);
      setLandingPage(prev => ({
        ...prev,
        testimonials: prev.testimonials.filter(t => t.id !== id)
      }));
      toast.success('Testimonial deleted!');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleAddBenefit = () => {
    if (!newBenefit.title || !newBenefit.description) {
      toast.error('Please fill in both title and description');
      return;
    }
    setLandingPage(prev => ({
      ...prev,
      benefits: [...(prev.benefits || []), { ...newBenefit, id: Date.now().toString() }]
    }));
    setNewBenefit({ title: '', description: '' });
  };

  const handleRemoveBenefit = (index) => {
    setLandingPage(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-background" data-testid="admin-settings">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 w-64 h-full flex flex-col sidebar-dark transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-primary-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center">
                <span className="text-white font-serif text-xl font-semibold">M</span>
              </div>
              <div>
                <span className="font-serif text-xl text-white block">Medichi Tribe</span>
                <span className="text-xs text-primary-400">Admin Portal</span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 rounded-lg text-primary-200 hover:bg-primary-800 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            <button
              onClick={() => navigate('/admin')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
            >
              <LayoutDashboard className="h-5 w-5" />
              Dashboard
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
            >
              <Users className="h-5 w-5" />
              Marketers
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary-800 text-white"
            >
              <Settings className="h-5 w-5" />
              Settings
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-primary-800">
          <div className="px-4 py-3 mb-3">
            <p className="text-primary-300 text-sm">Admin</p>
            <p className="text-white font-medium truncate">{user?.full_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-neutral-border px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-neutral-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg hover:bg-neutral-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="font-heading text-xl sm:text-2xl font-semibold text-primary-900">Settings</h1>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {/* Section Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={activeSection === 'training' ? 'default' : 'outline'}
              onClick={() => setActiveSection('training')}
              className="rounded-xl"
            >
              <Video className="h-4 w-4 mr-2" />
              Training Video
            </Button>
            <Button
              variant={activeSection === 'landing' ? 'default' : 'outline'}
              onClick={() => setActiveSection('landing')}
              className="rounded-xl"
            >
              <FileEdit className="h-4 w-4 mr-2" />
              Edit Landing Page
            </Button>
            <Button
              variant={activeSection === 'security' ? 'default' : 'outline'}
              onClick={() => setActiveSection('security')}
              className="rounded-xl"
            >
              <Lock className="h-4 w-4 mr-2" />
              Security
            </Button>
          </div>

          {/* Training Video Section */}
          {activeSection === 'training' && (
            <Card className="border-neutral-border shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5 text-purple-500" />
                  Training Video
                </CardTitle>
                <CardDescription>
                  Set a YouTube video that will be displayed on all marketer dashboards
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="training-url">YouTube Video URL</Label>
                  <Input
                    id="training-url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={trainingVideoUrl}
                    onChange={(e) => setTrainingVideoUrl(e.target.value)}
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                
                {trainingVideoUrl && getYouTubeEmbedUrl(trainingVideoUrl) && (
                  <div className="mt-4">
                    <Label>Preview</Label>
                    <div className="relative w-full max-w-lg pt-[56.25%] rounded-xl overflow-hidden bg-black mt-1.5">
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={getYouTubeEmbedUrl(trainingVideoUrl)}
                        title="Training Video Preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSaveTrainingVideo}
                  disabled={savingVideo}
                  className="rounded-xl bg-primary-900 hover:bg-primary-800"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingVideo ? 'Saving...' : 'Save Training Video'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* WhatsApp Group Link Section */}
          {activeSection === 'training' && (
            <Card className="border-neutral-border shadow-card mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-green-500" />
                  WhatsApp Group Link
                </CardTitle>
                <CardDescription>
                  Set the WhatsApp group link that appears on all marketer dashboards
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="whatsapp-link">WhatsApp Group Link</Label>
                  <Input
                    id="whatsapp-link"
                    placeholder="https://chat.whatsapp.com/..."
                    value={whatsAppGroupLink}
                    onChange={(e) => setWhatsAppGroupLink(e.target.value)}
                    className="mt-1.5 rounded-xl"
                    data-testid="whatsapp-link-input"
                  />
                  <p className="text-xs text-neutral-500 mt-1">This link will be shown to all marketers on their dashboard</p>
                </div>
                <Button
                  onClick={handleSaveWhatsApp}
                  disabled={savingWhatsApp}
                  className="rounded-xl bg-green-600 hover:bg-green-700"
                  data-testid="save-whatsapp-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingWhatsApp ? 'Saving...' : 'Save WhatsApp Link'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Landing Page Editor Section */}
          {activeSection === 'landing' && (
            <div className="space-y-6">
              {/* Preview Button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => window.open('/join', '_blank')}
                  className="rounded-xl"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview Landing Page
                </Button>
              </div>

              {/* Founder Image */}
              <Card className="border-neutral-border shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-amber-500" />
                    Founder Image
                  </CardTitle>
                  <CardDescription>Upload a photo of Ekene Medichi for the About section</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="file"
                    ref={founderImageInputRef}
                    onChange={handleFounderImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  
                  {founderImageUrl && (
                    <div className="flex items-center gap-4">
                      <img 
                        src={founderImageUrl.startsWith('/api') ? `${API_BASE}${founderImageUrl}` : founderImageUrl}
                        alt="Founder" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-amber-200"
                      />
                      <p className="text-sm text-neutral-500">Current founder image</p>
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => founderImageInputRef.current?.click()}
                    disabled={uploadingFounder}
                    className="rounded-xl"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingFounder ? 'Uploading...' : founderImageUrl ? 'Change Image' : 'Upload Image'}
                  </Button>
                </CardContent>
              </Card>

              {/* Main Content */}
              <Card className="border-neutral-border shadow-card">
                <CardHeader>
                  <CardTitle>Page Content</CardTitle>
                  <CardDescription>Edit the main text content of your landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="headline">Headline</Label>
                    <Input
                      id="headline"
                      placeholder="Join the Medichi Tribe"
                      value={landingPage.headline || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, headline: e.target.value }))}
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subheadline">Subheadline</Label>
                    <Input
                      id="subheadline"
                      placeholder="Build your real estate empire with us"
                      value={landingPage.subheadline || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, subheadline: e.target.value }))}
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero-description">Hero Description (below headline)</Label>
                    <Textarea
                      id="hero-description"
                      placeholder="Join 1,000+ Nigerians already earning 6 and 7 figures in commissions monthly..."
                      value={landingPage.hero_description || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, hero_description: e.target.value }))}
                      className="mt-1.5 rounded-xl min-h-[100px]"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      This text appears below the main headline on the landing page
                    </p>
                  </div>
                  <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 space-y-3">
                    <Label htmlFor="hero-video-url" className="text-amber-900 font-semibold flex items-center gap-2">
                      <Video className="h-4 w-4" /> Opportunity Video (top of landing page)
                    </Label>
                    <Input
                      id="hero-video-url"
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=... or Vimeo / Facebook / direct MP4 URL"
                      value={landingPage.hero_video_url || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, hero_video_url: e.target.value }))}
                      className="rounded-xl"
                      data-testid="hero-video-url-input"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        onClick={handleSaveHeroVideo}
                        disabled={savingHeroVideo}
                        className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="apply-hero-video-btn"
                      >
                        {savingHeroVideo ? 'Applying…' : (landingPage.hero_video_url ? 'Apply Video to Landing Page' : 'Remove Video')}
                      </Button>
                      {landingPage.hero_video_url && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setLandingPage(prev => ({ ...prev, hero_video_url: '' }));
                          }}
                          className="rounded-full"
                          data-testid="clear-hero-video-btn"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-amber-900/80">
                      Supported: YouTube, Vimeo, Facebook video links, or direct .mp4/.webm URLs. Click <strong>Apply Video</strong> to publish it — the general "Save Landing Page" button is only for the other fields.
                    </p>

                    {landingPage.hero_video_url && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-neutral-700 mb-1.5">Live preview (what visitors will see):</p>
                        <div className="rounded-xl overflow-hidden border-2 border-amber-300 bg-black" data-testid="hero-video-preview">
                          <div className="relative w-full pt-[56.25%]">
                            {getEmbedUrl(landingPage.hero_video_url) ? (
                              <iframe
                                className="absolute top-0 left-0 w-full h-full"
                                src={getEmbedUrl(landingPage.hero_video_url)}
                                title="Opportunity video preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            ) : isDirectVideoUrl(landingPage.hero_video_url) ? (
                              <video className="absolute top-0 left-0 w-full h-full" src={landingPage.hero_video_url} controls playsInline />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4 text-sm">
                                We couldn't recognise this as a video URL. Paste a YouTube, Vimeo, Facebook video, or direct .mp4 link.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4 space-y-3">
                    <div>
                      <Label htmlFor="meta-pixel-id" className="text-blue-900 font-semibold">Meta Pixel ID (Facebook / Instagram Ads)</Label>
                      <Input
                        id="meta-pixel-id"
                        type="text"
                        placeholder="e.g. 1234567890123456"
                        value={landingPage.meta_pixel_id || ''}
                        onChange={(e) => setLandingPage(prev => ({ ...prev, meta_pixel_id: e.target.value }))}
                        className="mt-1.5 rounded-xl"
                        data-testid="meta-pixel-id-input"
                      />
                      <p className="text-xs text-neutral-600 mt-1">
                        Paste just the 15-16 digit Pixel ID from Meta Events Manager. We'll auto-fire PageView on the landing page so Meta Ads can track visits and conversions.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="tracking-head-code" className="text-blue-900 font-semibold">Extra Tracking Code (optional)</Label>
                      <Textarea
                        id="tracking-head-code"
                        placeholder="<!-- Paste Google Analytics, TikTok Pixel, or any tracking snippet here -->"
                        value={landingPage.tracking_head_code || ''}
                        onChange={(e) => setLandingPage(prev => ({ ...prev, tracking_head_code: e.target.value }))}
                        className="mt-1.5 rounded-xl font-mono text-xs min-h-[100px]"
                        data-testid="tracking-head-code-input"
                      />
                      <p className="text-xs text-neutral-600 mt-1">
                        Advanced: paste any raw HTML/JS tracking snippet (GA4, TikTok, LinkedIn, etc.). It will be injected into the landing page on load. Only paste code from providers you trust.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="sales-copy">Sales Copy</Label>
                    <Textarea
                      id="sales-copy"
                      placeholder="Describe your opportunity..."
                      value={landingPage.sales_copy || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, sales_copy: e.target.value }))}
                      className="mt-1.5 rounded-xl min-h-[120px]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cta-text">CTA Button Text</Label>
                    <Input
                      id="cta-text"
                      placeholder="Register Now"
                      value={landingPage.cta_button_text || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, cta_button_text: e.target.value }))}
                      className="mt-1.5 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cta-link">CTA Button Link (Registration URL)</Label>
                    <Input
                      id="cta-link"
                      placeholder="/register or a marketer's referral link (e.g., /register?ref=ABC123)"
                      value={landingPage.cta_button_link || ''}
                      onChange={(e) => setLandingPage(prev => ({ ...prev, cta_button_link: e.target.value }))}
                      className="mt-1.5 rounded-xl"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      Use <code className="bg-neutral-100 px-1 rounded">/register</code> for default, or paste a specific marketer's referral link
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Benefits */}
              <Card className="border-neutral-border shadow-card">
                <CardHeader>
                  <CardTitle>Benefits Section</CardTitle>
                  <CardDescription>List the benefits of joining</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {landingPage.benefits?.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-primary-900">{benefit.title}</p>
                        <p className="text-sm text-neutral-600">{benefit.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveBenefit(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="border-t pt-4 space-y-3">
                    <Input
                      placeholder="Benefit title"
                      value={newBenefit.title}
                      onChange={(e) => setNewBenefit(prev => ({ ...prev, title: e.target.value }))}
                      className="rounded-xl"
                    />
                    <Input
                      placeholder="Benefit description"
                      value={newBenefit.description}
                      onChange={(e) => setNewBenefit(prev => ({ ...prev, description: e.target.value }))}
                      className="rounded-xl"
                    />
                    <Button onClick={handleAddBenefit} variant="outline" className="rounded-xl">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Benefit
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Testimonials */}
              <Card className="border-neutral-border shadow-card">
                <CardHeader>
                  <CardTitle>Testimonials & Social Proof</CardTitle>
                  <CardDescription>
                    Add multiple YouTube videos and images as testimonials. 
                    Currently showing: {landingPage.testimonials?.filter(t => t.type === 'youtube').length || 0} videos, {landingPage.testimonials?.filter(t => t.type === 'image').length || 0} images
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Existing testimonials list */}
                  {landingPage.testimonials?.length > 0 && (
                    <div className="space-y-3 pb-4 border-b">
                      <p className="text-sm font-medium text-neutral-700">Current Testimonials:</p>
                      {landingPage.testimonials.map((testimonial) => (
                        <div key={testimonial.id} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl">
                          <div className="w-8 h-8 rounded-lg bg-neutral-200 flex items-center justify-center flex-shrink-0">
                            {testimonial.type === 'youtube' ? (
                              <Youtube className="h-4 w-4 text-red-500" />
                            ) : (
                              <Image className="h-4 w-4 text-blue-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {testimonial.type === 'image' && testimonial.url && (
                              <img 
                                src={testimonial.url.startsWith('/api') ? `${API_BASE}${testimonial.url}` : testimonial.url}
                                alt="Testimonial" 
                                className="w-20 h-20 object-cover rounded-lg mb-2"
                              />
                            )}
                            <p className="text-sm text-neutral-600 truncate">{testimonial.url}</p>
                            {testimonial.caption && (
                              <p className="text-xs text-neutral-400 mt-1">{testimonial.caption}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTestimonial(testimonial.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add new testimonial section */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-neutral-700">Add New Testimonial:</p>
                    <div className="flex gap-2">
                      <Button
                        variant={newTestimonial.type === 'youtube' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setNewTestimonial(prev => ({ ...prev, type: 'youtube', url: '' }))}
                        className="rounded-xl"
                      >
                        <Youtube className="h-4 w-4 mr-1" />
                        YouTube Video
                      </Button>
                      <Button
                        variant={newTestimonial.type === 'image' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setNewTestimonial(prev => ({ ...prev, type: 'image', url: '' }))}
                        className="rounded-xl"
                      >
                        <Image className="h-4 w-4 mr-1" />
                        Image / Screenshot
                      </Button>
                    </div>
                    
                    {/* Hidden file input for image upload */}
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleTestimonialImageUpload}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                    />
                    
                    {newTestimonial.type === 'youtube' ? (
                      <Input
                        placeholder="YouTube URL (e.g., youtube.com/watch?v=... or youtube.com/shorts/...)"
                        value={newTestimonial.url}
                        onChange={(e) => setNewTestimonial(prev => ({ ...prev, url: e.target.value }))}
                        className="rounded-xl"
                      />
                    ) : (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="rounded-xl w-full justify-start"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingImage ? 'Uploading...' : newTestimonial.url ? 'Change Image' : 'Click to Upload Image'}
                        </Button>
                        {newTestimonial.url && (
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                            <img 
                              src={newTestimonial.url.startsWith('/api') ? `${API_BASE}${newTestimonial.url}` : newTestimonial.url}
                              alt="Preview" 
                              className="w-16 h-16 object-cover rounded"
                            />
                            <p className="text-sm text-green-700 font-medium">Image ready! Add caption and click "Add Testimonial" below.</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Input
                      placeholder="Caption (optional - describe the testimonial)"
                      value={newTestimonial.caption}
                      onChange={(e) => setNewTestimonial(prev => ({ ...prev, caption: e.target.value }))}
                      className="rounded-xl"
                    />
                    <Button 
                      onClick={handleAddTestimonial} 
                      className="rounded-xl bg-green-600 hover:bg-green-700 text-white w-full"
                      disabled={!newTestimonial.url}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Testimonial
                    </Button>
                    <p className="text-xs text-neutral-500 text-center">
                      You can add multiple testimonials. Each one will appear on the landing page.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleSaveLandingPage}
                disabled={savingLandingPage}
                className="rounded-xl bg-primary-900 hover:bg-primary-800"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingLandingPage ? 'Saving...' : 'Save Landing Page'}
              </Button>
            </div>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <Card className="border-neutral-border shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-amber-500" />
                  Update Admin Credentials
                </CardTitle>
                <CardDescription>
                  Change your username and password. You'll need to log in again after updating.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="new-username">New Username</Label>
                  <Input
                    id="new-username"
                    placeholder="Enter new username"
                    value={credentials.new_username}
                    onChange={(e) => setCredentials(prev => ({ ...prev, new_username: e.target.value }))}
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password (min 12 characters)"
                    value={credentials.new_password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, new_password: e.target.value }))}
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={credentials.confirm_password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                <div className="border-t pt-4">
                  <Label htmlFor="current-password">Current Password (required to make changes)</Label>
                  <Input
                    id="current-password"
                    type="password"
                    placeholder="Enter your current password"
                    value={credentials.current_password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, current_password: e.target.value }))}
                    className="mt-1.5 rounded-xl"
                  />
                </div>

                <Button
                  onClick={handleUpdateCredentials}
                  disabled={savingCredentials || !credentials.new_username || !credentials.new_password}
                  className="rounded-xl bg-amber-600 hover:bg-amber-700"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {savingCredentials ? 'Updating...' : 'Update Credentials'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
